import { useState } from 'react';
import { useModal } from '../contexts';
import TextInputField from './TextInputField';
import IconPickerField from './IconPickerField';
import ColorPickerField from './ColorPickerField';
import Modal from './Modal';
import { BasicWorkflow, Workflow } from '../interfaces';
import { useTranslation } from 'react-i18next';
import TextAreaField from './TextAreaField';
import SmallSelectField from './SmallSelectField';
import useUser from '../hooks/useUser';

interface Props {
  updateWorkflow: ((workflow: Workflow) => void) | ((workflow: BasicWorkflow) => void),
  workflow: Workflow,
  acceptText?: string,
  titleText?: string,
}

function WorkflowModalEdit(props: Props) {
  const [workflow, setWorkflow] = useState<BasicWorkflow>(props.workflow);
  const { t } = useTranslation();
  const { closeModal } = useModal();

  const { user } = useUser();

  return (
    <Modal
      title={props.titleText ?? t("Update Workflow")}
      primaryButtonProps={{
        text: props.acceptText ?? t('Update'),
        onClick: () => { props.updateWorkflow(workflow as Workflow); },
        disabled: workflow.name.trim() === '',
      }}
      secondaryButtonProps={{ text: t("Cancel"), onClick: closeModal }}
    >
      <TextInputField label="Workflow Name" value={workflow.name} onChange={(name) => setWorkflow((w: any) => ({ ...w, name }))} required />
      <div className='flex flex-col mt-2'>
        <div className="text-gray-600 font-gilroy font-semibold text-sm mb-2">
          Appearance
        </div>
        <div className="flex gap-4 items-start -mb-2">
          <ColorPickerField value={workflow.color} onChange={(color) => setWorkflow((w: any) => ({ ...workflow, color }))} />
          <IconPickerField value={workflow.icon} onChange={(icon) => setWorkflow((w: any) => ({ ...workflow, icon }))} selectColour={workflow.color} />
        </div>
      </div>
      <TextAreaField label={t('Workflow Description')} placeholder={t('Optionally provide a description for the purpose of this workflow')} value={workflow.description} onChange={(description) => setWorkflow((w: any) => ({ ...w, description }))} rows={3} optional />

      {/* NOTE: This does not currently show the owners' language which would be used in the backend. */}
      {!!workflow.id &&
        <div>
          <SmallSelectField className="w-72" label={t("Language")} value={workflow.language || user!.language} onChange={(language) => setWorkflow((w: any) => ({ ...w, language }))}>
            <option value={"en"}>{t("English")}</option>
            <option value={"es"}>{t("Spanish")}</option>
            <option value={"fr"}>{t("French")}</option>
            <option value={"de"}>{t("German")}</option>
          </SmallSelectField>
          <p className="text-xs text-gray-400 mt-1">{t("Sets the language for signature request emails, review notifications, and signed document confirmations.")}</p>
        </div>
      }
    </Modal>
  );
}

export default WorkflowModalEdit;
